import { useState, useEffect, useRef } from 'react'
import { X, Tag } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

export default function BulkTagModal({ items, idKey, onClose, onComplete }) {
  // items: array of { id, title } where id is stream_uid or archive_key
  // idKey: 'stream_uid' or 'archive_key' for resolving content
  const { api } = useAuth()
  const [tags, setTags] = useState([])
  const [input, setInput] = useState('')
  const [allTags, setAllTags] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [mode, setMode] = useState('add') // 'add' or 'replace'
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] })
  const inputRef = useRef()

  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (input.trim()) {
      const matches = allTags.filter(t =>
        t.includes(input.toLowerCase()) && !tags.includes(t)
      )
      setSuggestions(matches.slice(0, 8))
    } else {
      setSuggestions([])
    }
  }, [input, allTags, tags])

  function addTag(tag) {
    const t = tag.trim().toLowerCase()
    if (t && !tags.includes(t)) {
      setTags(prev => [...prev, t])
    }
    setInput('')
    setSuggestions([])
    inputRef.current?.focus()
  }

  function removeTag(tag) {
    setTags(prev => prev.filter(t => t !== tag))
  }

  function handleKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault()
      addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      setTags(prev => prev.slice(0, -1))
    }
    if (e.key === 'Escape') {
      onClose()
    }
  }

  async function handleApply() {
    if (tags.length === 0) return
    setProcessing(true)
    setProgress({ done: 0, total: items.length, errors: [] })

    for (let i = 0; i < items.length; i++) {
      try {
        // Get tbtv_id — skip resolve if the item already has one
        let tbtvId = null
        if (items[i].id?.startsWith('tbtv_')) {
          tbtvId = items[i].id
        } else {
          const resolveBody = { title: items[i].title }
          resolveBody[idKey] = items[i].id
          const resolved = await api('/content/resolve', {
            method: 'POST',
            body: JSON.stringify(resolveBody)
          })
          tbtvId = resolved.tbtv_id
        }

        let finalTags = tags
        if (mode === 'add') {
          const existing = await api(`/tags/${tbtvId}`)
          const existingTags = existing.tags || []
          finalTags = [...new Set([...existingTags, ...tags])]
        }

        await api(`/tags/${tbtvId}`, {
          method: 'PUT',
          body: JSON.stringify({ tags: finalTags })
        })
      } catch (e) {
        setProgress(prev => ({
          ...prev,
          errors: [...prev.errors, { item: items[i].title, error: e.message }]
        }))
      }
      setProgress(prev => ({ ...prev, done: i + 1 }))
    }

    setProcessing(false)
    if (onComplete) onComplete()
  }

  return (
    <div className="bulk-modal-overlay" onClick={onClose}>
      <div className="bulk-modal" onClick={e => e.stopPropagation()}>
        <div className="bulk-modal-header">
          <Tag size={16} />
          <h3>Bulk Tag - {items.length} item{items.length !== 1 ? 's' : ''}</h3>
          <button onClick={onClose} className="bulk-modal-close"><X size={16} /></button>
        </div>

        <div className="bulk-modal-body">
          <div className="bulk-tag-mode">
            <label className={mode === 'add' ? 'active' : ''}>
              <input type="radio" name="mode" checked={mode === 'add'} onChange={() => setMode('add')} />
              Add to existing tags
            </label>
            <label className={mode === 'replace' ? 'active' : ''}>
              <input type="radio" name="mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
              Replace all tags
            </label>
          </div>

          <div className="bulk-tag-input-area">
            <div className="bulk-tag-chips">
              {tags.map(t => (
                <span key={t} className="bulk-tag-chip">
                  {t}
                  <button onClick={() => removeTag(t)}><X size={10} /></button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tags.length ? '' : 'Type tags...'}
                autoFocus
              />
            </div>
            {suggestions.length > 0 && (
              <div className="bulk-tag-suggestions">
                {suggestions.map(s => (
                  <button key={s} onClick={() => addTag(s)}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <div className="bulk-tag-items">
            <span className="bulk-tag-items-label">Applying to:</span>
            <div className="bulk-tag-items-list">
              {items.slice(0, 10).map((item, i) => (
                <span key={i} className="bulk-tag-item-name">{item.title}</span>
              ))}
              {items.length > 10 && (
                <span className="bulk-tag-item-more">+{items.length - 10} more</span>
              )}
            </div>
          </div>

          {processing && (
            <div className="bulk-tag-progress">
              <div className="bulk-tag-progress-bar">
                <div
                  className="bulk-tag-progress-fill"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <span>{progress.done} / {progress.total}</span>
              {progress.errors.length > 0 && (
                <span className="bulk-tag-errors">{progress.errors.length} errors</span>
              )}
            </div>
          )}
        </div>

        <div className="bulk-modal-footer">
          <button onClick={onClose} className="secondary" disabled={processing}>Cancel</button>
          <button onClick={handleApply} disabled={processing || tags.length === 0}>
            {processing ? `Processing ${progress.done}/${progress.total}...` : `Apply Tags`}
          </button>
        </div>
      </div>
    </div>
  )
}
